import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets-lqy194kg.emergentagent.net/job_clearout-pro/artifacts/h0xv5ks4_JUNK%20REMOVAL%20MD%20Transparent.webp";
const PHONE_NUMBER = "(918) 321-0654";
const PHONE_HREF = "tel:9183210654";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Reviews', path: '/reviews' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <>
      {/* Announcement Bar */}
      <div className="announcement-bar py-2 text-center text-sm font-medium" data-testid="announcement-bar">
        <span className="inline-flex items-center gap-2">
          <span className="text-yellow-300">★★★★★</span>
          <span>5 Stars on Google · 85 Reviews</span>
        </span>
      </div>

      {/* Main Header - Dark background */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 bg-[#1a1a1a] ${
          isScrolled ? 'shadow-md' : ''
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" data-testid="logo-link">
              <img
                src={LOGO_URL}
                alt="Junk Removal M.D."
                className="h-16 md:h-20 w-auto"
                loading="eager"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-semibold text-sm uppercase tracking-wide transition-colors hover:text-[#dc2626] ${
                    location.pathname === link.path ? 'text-[#dc2626]' : 'text-white'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <a
                href={PHONE_HREF}
                className="flex items-center gap-2 font-bold text-white hover:text-[#dc2626] transition-colors"
                data-testid="header-phone"
              >
                <Phone className="w-5 h-5" />
                <span>{PHONE_NUMBER}</span>
              </a>
              <Link to="/contact">
                <Button
                  className="bg-[#dc2626] hover:bg-[#b91c1c] text-white font-bold uppercase tracking-wider px-6 btn-press"
                  data-testid="header-cta-btn"
                >
                  Free Estimate
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button & Phone */}
            <div className="flex lg:hidden items-center gap-3">
              <a
                href={PHONE_HREF}
                className="flex items-center justify-center w-10 h-10 bg-[#dc2626] rounded-full text-white btn-press"
                aria-label="Call us"
                data-testid="mobile-call-btn"
              >
                <Phone className="w-5 h-5" />
              </a>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-white"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                data-testid="mobile-menu-toggle"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t" data-testid="mobile-menu">
            <nav className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-3 px-4 font-semibold text-sm uppercase tracking-wide rounded transition-colors ${
                    location.pathname === link.path
                      ? 'bg-[#f5f5f5] text-[#dc2626]'
                      : 'text-[#1a1a1a] hover:bg-[#f5f5f5]'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t">
                <Link to="/contact" className="block">
                  <Button
                    className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white font-bold uppercase tracking-wider btn-press"
                    data-testid="mobile-cta-btn"
                  >
                    Request Free Estimate
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
